import { useQueries } from '@tanstack/react-query'
import { merge } from 'lodash'
import { useMemo } from 'react'
import {
  type OpalApiRequestParams,
  type OpalApiResponseDocument,
} from '@opallabs/api'
import { globalQueryKeys } from '../../queryKeys'
import { getReadBlocksQueryFn, type UseReadBlocksParams } from '.'

type Request = OpalApiRequestParams<'ReadBlocksBFFV1'>
type Query = {
  filter: NonNullable<Request['query']['filter']>
}
type Response = OpalApiResponseDocument<'ReadBlocksBFFV1', 200>

type HookParams<TSelectResult = Response> = Omit<
  UseReadBlocksParams,
  'filter'
> &
  Readonly<{
    planId: NonNullable<NonNullable<Request['query']['filter']>['plan_id']>
    select?: (data: Response) => TSelectResult
    /**
     * When `true`, the hook throws a promise while loading so a parent `<Suspense>` boundary can render a fallback.
     */
    suspense?: boolean
  }>

/**
 * Fetch all the block templates relevant to a given plan, combining workspace-wide templates with those specific to the
 * plan.
 *
 * @note **DO NOT** use `readBlocksBFFSuccess` to mock this hook’s response — you’ll get duplicated data because of the
 * two queries. Instead use `readPlanTemplateBlocksBFFSuccess` and `readWorkspaceTemplateBlocksBFFSuccess`.
 */
export const useReadBlockTemplatesForPlan = <TSelectResult = Response>({
  enabled,
  planId,
  select,
  suspense,
  workspaceUuid,
}: HookParams<TSelectResult>) => {
  const baseQuery: Query = {
    filter: { is_template: true },
  } as const
  const planQuery: Query = merge({}, baseQuery, {
    filter: {
      plan_id: planId,
    },
  } satisfies Query)
  const workspaceQuery: Query = merge({}, baseQuery, {
    filter: {
      is_workspace_wide_template: true,
    },
  } satisfies Query)

  const [workspaceResult, planResult] = useQueries({
    queries: [
      {
        enabled,
        queryFn: getReadBlocksQueryFn(workspaceQuery, workspaceUuid),
        queryKey: globalQueryKeys.blocksByWorkspace(workspaceUuid, baseQuery),
        retry: 3,
        suspense,
      },
      {
        enabled: enabled && !!planId,
        queryFn: getReadBlocksQueryFn(planQuery, workspaceUuid),
        queryKey: globalQueryKeys.blocksByPlan(
          workspaceUuid,
          planId,
          baseQuery
        ),
        retry: 3,
        suspense,
      },
    ],
  })

  return useMemo(() => {
    // The workspace query returns the plan's own workspace-wide templates, and needs to be de-duped
    const workspaceTemplateIds = new Set(
      (workspaceResult.data?.data ?? []).map((block) => block.id)
    )
    const uniquePlanBlocks = (planResult.data?.data ?? []).filter(
      (block) => !workspaceTemplateIds.has(block.id)
    )
    const combined: Response = {
      data: [...(workspaceResult.data?.data ?? []), ...uniquePlanBlocks],
    }

    return {
      data: select ? select(combined) : (combined as TSelectResult),
      error: workspaceResult.error ?? planResult.error,
      isError: workspaceResult.isError || planResult.isError,
      isLoading: workspaceResult.isLoading || planResult.isLoading,
    }
  }, [
    planResult.data?.data,
    planResult.error,
    planResult.isError,
    planResult.isLoading,
    select,
    workspaceResult.data?.data,
    workspaceResult.error,
    workspaceResult.isError,
    workspaceResult.isLoading,
  ])
}
